/**
 * Mailbox-scale math (issue #858 dashboard mailbox-progress widget).
 *
 * Single source of truth for the "About N more emails" / "~T" whole-mailbox
 * estimate shown on the Dashboard mailbox-progress widget AND reused for
 * Post-Sample Pro Nurture email personalization. Deliberately lives in
 * `@purebox/shared` (not duplicated per-surface) so every surface that shows
 * this number is guaranteed to show the exact same honest approximation -
 * never independently re-derived, never a different rounding/threshold.
 *
 * Truthfulness constraints (do not weaken):
 * - Always truncates mailbox totals to the nearest thousand (for example
 *   "15,000", never an exact raw count with false precision).
 * - Returns null below `MAILBOX_SCALE_MIN_MESSAGES` rather than surfacing a
 *   trivially small/noisy number.
 * - The time ceiling is a flat `SECONDS_PER_SUGGESTION` rate applied to the
 *   rounded scale - never an extrapolation of the sample's own observed
 *   clutter rate, which would vary wildly with sample recency/size.
 */

import { SECONDS_PER_SUGGESTION } from '../constants'
import { FREE_SAMPLE } from '../lib/free-tier'
import type { MailboxTotals } from '../schemas/scan'

/**
 * Below this many in-scope messages the "about N emails" framing adds no
 * value, so surfaces fall back to their generic copy instead.
 */
export const MAILBOX_SCALE_MIN_MESSAGES = 3000

const SECONDS_PER_HOUR = 3600
const HOURS_PER_DAY = 24

/** Hour count at/above which the potential-time ceiling switches to whole days. */
const HOURS_TO_DAYS_THRESHOLD = 100

/**
 * Truncate to the nearest thousand so the widget shows a cleaner, less precise
 * estimate like "15,000" instead of the raw mailbox total.
 */
function toThousandsFloor(value: number): number {
  return Math.max(0, Math.floor(value / 1000) * 1000)
}

/**
 * Rounds to two significant figures so the time ceiling stays an honest
 * approximation (for example "13 hrs") without carrying raw decimal noise.
 */
function toTwoSignificantFigures(value: number): number {
  const digits = Math.floor(Math.log10(value)) + 1
  const unit = 10 ** Math.max(0, digits - 2)
  return Math.round(value / unit) * unit
}

/**
 * Returns the raw, already-truncated scope total, or null under the same
 * missing/non-finite/below-threshold conditions as
 * `formatMailboxScopeMessagesTotal`. Useful when a caller needs the numeric
 * value (e.g. to subtract the free-sample size) rather than the formatted
 * "12,000"-style string.
 */
export function getRoundedMailboxScopeMessagesTotal(
  scopeMessagesTotal: number | null | undefined,
): number | null {
  if (scopeMessagesTotal == null || !Number.isFinite(scopeMessagesTotal)) return null
  if (scopeMessagesTotal < MAILBOX_SCALE_MIN_MESSAGES) return null
  return toThousandsFloor(scopeMessagesTotal)
}

/**
 * Formats the scan-scope total for user-facing scale copy, or returns null when
 * the cached totals are missing or too small to be worth quoting.
 *
 * Only `scope_messages_total` may be surfaced. Gmail's raw `messages_total`
 * includes Sent, Drafts, Spam, and Trash, which PureBox never scans.
 */
export function formatMailboxScale(totals: MailboxTotals | null | undefined): string | null {
  return formatMailboxScopeMessagesTotal(totals?.scope_messages_total)
}

export function formatMailboxScopeMessagesTotal(
  scopeMessagesTotal: number | null | undefined,
): string | null {
  const rounded = getRoundedMailboxScopeMessagesTotal(scopeMessagesTotal)
  if (rounded === null) return null
  return new Intl.NumberFormat('en-US').format(rounded)
}

/**
 * Renders an hours-or-days duration string at two significant figures, with
 * correct singular forms ("1 hr", "1 day").
 */
export function formatHoursOrDaysCeiling(totalHours: number): string {
  if (totalHours < HOURS_TO_DAYS_THRESHOLD) {
    const hrs = toTwoSignificantFigures(totalHours)
    return `${hrs} ${hrs === 1 ? 'hr' : 'hrs'}`
  }

  const days = toTwoSignificantFigures(totalHours / HOURS_PER_DAY)
  return `${days} ${days === 1 ? 'day' : 'days'}`
}

/**
 * Formats the flat-rate time ceiling for Pro's additional mailbox reach, or
 * returns null under exactly the same conditions as
 * `formatMailboxScopeMessagesTotal` (missing, non-finite, or below
 * `MAILBOX_SCALE_MIN_MESSAGES`).
 *
 * The ceiling is computed from the ALREADY-TRUNCATED scope total (the same
 * thousand-floor value `formatMailboxScale` would display) multiplied by
 * `SECONDS_PER_SUGGESTION`. This is a flat-rate estimate, never derived from
 * the sample's observed per-email rate.
 */
export function formatMailboxPotentialTime(
  scopeMessagesTotal: number | null | undefined,
): string | null {
  const roundedScale = getRoundedMailboxScopeMessagesTotal(scopeMessagesTotal)
  if (roundedScale === null) return null

  const totalSeconds = roundedScale * SECONDS_PER_SUGGESTION
  const totalHours = totalSeconds / SECONDS_PER_HOUR

  return formatHoursOrDaysCeiling(totalHours)
}

/**
 * The additional emails Pro can review beyond the free sample: the rounded
 * mailbox scale minus the free-sample's fixed classify ceiling
 * (`FREE_SAMPLE.classifyTotal`), NOT the actual per-scan `sampleTotal` (which
 * varies scan to scan) - this keeps the number stable.
 */
export function computeMailboxPotentialCount(roundedMailboxScale: number): number {
  return Math.max(0, roundedMailboxScale - FREE_SAMPLE.classifyTotal)
}
